import React, { lazy, Suspense, useCallback, useEffect } from 'react';
import cx from 'classnames';

import {
  BetManager,
  BetTypeProvider,
  Bus,
  debounce,
  globalBetslipStateUpdate,
  isEmptyCollection,
  Logger,
  useApplicationState,
  useBetslipState,
  useBetslipStateUpdate,
} from '@atlas/core';
import { BetslipErrors, MoneyInput } from '@atlas/ui';

import useBonuses from 'common/Betslip/useBonuses';
import useSelectedBonuses from 'common/Betslip/useSelectedBonuses';

import PostMessageActionButton from './PostMessageActionButton';

const MultipleControls = lazy(
  () => import(/* webpackChunkName: "Betslip" */ 'common/Betslip/MultipleControls'),
);
const PlaceBetButton = lazy(
  () => import(/* webpackChunkName: "Betslip" */ 'common/Betslip/BetsMoneyBlock/PlaceBetButton'),
);
const GetMaxBetButton = lazy(
  () => import(/* webpackChunkName: "Betslip" */ 'common/Betslip/BetsMoneyBlock/GetMaxBetButton'),
);
const InfoSection = lazy(
  () => import(/* webpackChunkName: "Betslip" */ 'common/Betslip/BetsMoneyBlock/InfoSection'),
);

const betAmountMaxValue = 9223e13;
const emptySelectedBet = {};

const broadcastSelectedBet = debounce(payload => Bus.emit('ui:selected_bet', payload), 100);

const IFrameDefaultControls = ({
  currency,
  placeBetDisabled,
  selectedBetTypeIndex,
  setSelectedBetTypeIndex,
  vip,
}) => {
  const { amount, betItems, betTypes, bonuses, loading } = useBetslipState();
  const [active, setActive] = React.useState(false);
  const [placeBetCall, togglePlaceBetCall] = React.useState(false);
  const betslipLoading = loading || placeBetCall;

  const { authenticated } = useApplicationState();

  const isSingleBet = betItems && betItems.length === 1;

  // Selected bet type or single bet item
  const selectedBet = betTypes[selectedBetTypeIndex] || betItems[0] || emptySelectedBet;

  const { freebetBonuses, multipleBonuses } = useBonuses(bonuses, selectedBet);

  const { removeSelectedBonuses, selectBonus, selectedBonuses, selectedFreebet } =
    useSelectedBonuses();

  // Clean up reports if user adding bet item to betslip
  useEffect(() => {
    if (betItems.length > 0) {
      globalBetslipStateUpdate({ reports: [] });
    }

    // Removing selected bonuses when betslip changes
    removeSelectedBonuses();
  }, [betItems.length]);

  React.useEffect(() => {
    broadcastSelectedBet(selectedBet);
  }, [selectedBet]);

  const betslipStateUpdate = useBetslipStateUpdate();

  const isBetItemsActive = React.useMemo(
    () => betItems.every(bet => bet.active && !bet.suspended),
    [betItems],
  );

  const hasInactiveBets = React.useMemo(
    () => betItems.some(bet => !bet.active || bet.suspended),
    [betItems],
  );

  const betAmountChangeHandle = useCallback(
    amount => {
      if (selectedFreebet) {
        return;
      }

      if (amount > betAmountMaxValue) {
        Logger.info('bet amount is too big');
        return;
      }

      betslipStateUpdate({ amount });
    },
    [betslipStateUpdate, selectedFreebet],
  );

  const handlePlaceBet = useCallback(
    params => {
      const bet = {
        ...selectedBet,
        ...params,
        amount,
      };

      // Adding selected bonuses
      if (!isEmptyCollection(selectedBonuses)) {
        // Setting bonuses
        bet.bonusInfoList = selectedBonuses;

        if (selectedFreebet) {
          bet.amount = selectedFreebet.freeBetAmount;
        }
      }

      // Placing bets
      BetManager.placeBets([bet], { id: 'main', vip });
    },
    [selectedBet, selectedBonuses, selectedFreebet, amount, vip],
  );

  const preHandlePlaceBet = React.useCallback(() => {
    if (hasInactiveBets) {
      const activeBetItems = betItems.filter(bet => {
        if (!bet.active || bet.suspended) {
          BetManager.removeBet(bet);
          return;
        }

        return bet;
      });

      if (activeBetItems.length && amount) {
        togglePlaceBetCall(true);
      }
    } else {
      handlePlaceBet();
    }
  }, [hasInactiveBets, betItems, amount, handlePlaceBet]);

  React.useEffect(() => {
    const validationSuccessHandler = response => {
      togglePlaceBetCall(false);
      const { odds, outcomes, requestId } = response.bets[0];

      handlePlaceBet({
        odds,
        outcomes,
        requestId,
      });
      Bus.off(Bus.events.bet.validationSuccess, validationSuccessHandler);
    };

    const validationErrorHandler = () => {
      togglePlaceBetCall(false);
      Bus.off(Bus.events.bet.validationError, validationErrorHandler);
    };

    if (placeBetCall) {
      Bus.on(Bus.events.bet.validationSuccess, validationSuccessHandler);
      Bus.on(Bus.events.bet.validationError, validationErrorHandler);
    }

    return () => {
      Bus.off(Bus.events.bet.validationSuccess, validationSuccessHandler);
      Bus.off(Bus.events.bet.validationError, validationErrorHandler);
    };
  }, [placeBetCall]);

  const hasSelectedFreeBet = !isEmptyCollection(selectedFreebet);

  const inputClassname = cx(hasSelectedFreeBet && 'has-value');
  const bottomButtonContainerCls = cx('bottom-button-container');

  return (
    <div className="betslip-classic-controls">
      <BetslipErrors />

      {!isSingleBet && (
        <Suspense fallback={<div />}>
          <BetTypeProvider betType={selectedBet} key={selectedBet.id}>
            <MultipleControls
              selectedBet={selectedBet}
              selectedBetTypeIndex={selectedBetTypeIndex}
              setSelectedBetTypeIndex={setSelectedBetTypeIndex}
            />
          </BetTypeProvider>
        </Suspense>
      )}

      <div className="input-max-button-container">
        <div className="input-max-button">
          <MoneyInput
            active={active}
            className={inputClassname}
            currency={currency}
            disabled={betslipLoading || hasSelectedFreeBet}
            label="Amount"
            onBlur={() => setActive(false)}
            onChange={betAmountChangeHandle}
            onFocus={() => setActive(true)}
            value={selectedFreebet?.freeBetAmount || amount}
          />
          {authenticated && isBetItemsActive && (
            <Suspense fallback={<div />}>
              <GetMaxBetButton
                betItems={betItems}
                disabled={betslipLoading || hasSelectedFreeBet}
                loadingBtnMax={betslipLoading}
                selectedBet={selectedBet}
                setBetAmount={betAmountChangeHandle}
              />
            </Suspense>
          )}
        </div>
      </div>

      <Suspense fallback={<div />}>
        <InfoSection
          betAmount={selectedFreebet?.freeBetAmount || amount}
          currency={currency}
          freebetBonuses={freebetBonuses}
          isSingleBet={isSingleBet}
          multipleBonuses={multipleBonuses}
          removeSelectedBonuses={removeSelectedBonuses}
          selectBonus={selectBonus}
          selectedBet={selectedBet}
          selectedFreebet={selectedFreebet}
        />
      </Suspense>

      <div className={bottomButtonContainerCls}>
        {authenticated ? (
          <Suspense fallback={<div />}>
            <PlaceBetButton
              betAmount={amount}
              currency={currency}
              disabled={placeBetDisabled}
              hasInactiveBets={hasInactiveBets}
              loading={betslipLoading}
              onSubmit={preHandlePlaceBet}
              selectedFreebet={selectedFreebet}
              vip={vip}
            />
          </Suspense>
        ) : (
          <PostMessageActionButton
            action="ui:signin"
            message="betslip_place_auth_required"
            size="big"
          />
        )}
      </div>
    </div>
  );
};

export default IFrameDefaultControls;
